import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import Layout from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { PhoneCall, Loader2, Save, CheckCircle2, Eye, EyeOff, Zap, ExternalLink, BadgeCheck, AlertTriangle, Plus, Trash2, RefreshCw, Route, Layers, Webhook, Copy } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";

// Per-call-type agent routing (Retell only).
const ROUTE_KINDS = ["default", "absent", "birthday", "announcement"];
const ROUTE_LABELS = {
  default: "Default (fallback)",
  absent: "Absence calls",
  birthday: "Birthday calls",
  announcement: "Announcement calls",
};

export default function DevAdminVoiceConfig({ embedded = false }) {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("retell");
  const [form, setForm] = useState({});       // pid -> {nonSecretKey: value}
  const [secrets, setSecrets] = useState({});  // pid -> {secretKey: newValue}
  const [reveal, setReveal] = useState({});    // "pid.key" -> bool
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [newLabel, setNewLabel] = useState("");
  const [newType, setNewType] = useState("webhook");
  const [newDocs, setNewDocs] = useState("");
  const [adding, setAdding] = useState(false);
  const [removeTarget, setRemoveTarget] = useState(null);
  const [testNumber, setTestNumber] = useState("");
  const [placingCall, setPlacingCall] = useState(false);
  const [routes, setRoutes] = useState(null);         // {default:{agent_id,from_number,voice_hint}, ...}
  const [agents, setAgents] = useState([]);           // [{agent_id, name}]
  const [loadingAgents, setLoadingAgents] = useState(false);
  const [savingRoutes, setSavingRoutes] = useState(false);
  const [autoRetry, setAutoRetry] = useState(null);   // {enabled, delay_min}
  const [savingRetry, setSavingRetry] = useState(false);
  const [callQueue, setCallQueue] = useState(null);   // {max_parallel, default, max_allowed, webhook_path}
  const [savingQueue, setSavingQueue] = useState(false);

  useEffect(() => {
    if (user && !user.is_dev_admin) navigate("/dashboard", { replace: true });
  }, [user, navigate]);

  const hydrate = (d) => {
    const f = {}; const s = {};
    for (const p of d.providers) {
      f[p.id] = {}; s[p.id] = {};
      for (const fld of p.fields) {
        if (fld.secret) { s[p.id][fld.key] = ""; }
        else { f[p.id][fld.key] = p.values[fld.key] || ""; }
      }
    }
    setForm(f); setSecrets(s);
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/devadmin/voice-config");
      setData(data);
      hydrate(data);
      setTab(data.active_provider || "retell");
    } catch (e) {
      toast.error(apiErr(e.response?.data?.detail) || "Failed to load voice config");
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const provider = useMemo(() => data?.providers?.find((p) => p.id === tab), [data, tab]);

  const save = async (makeActive = false) => {
    if (!provider) return;
    setSaving(true);
    try {
      const values = { ...form[provider.id] };
      for (const [k, v] of Object.entries(secrets[provider.id] || {})) {
        if (v && v.trim()) values[k] = v.trim();
      }
      // Persist the edited values first.
      const { data: saved } = await api.put("/devadmin/voice-config", { provider_id: provider.id, values });
      setData(saved); hydrate(saved);
      if (makeActive) {
        // Verify the connection BEFORE accepting it as the active provider.
        try {
          const { data: t } = await api.post(`/devadmin/voice-config/test/${provider.id}`);
          toast.success(`Connection OK — ${t.detail}`);
        } catch (e) {
          toast.error(`Not activated — connection test failed: ${apiErr(e.response?.data?.detail) || "error"}`);
          return;
        }
        const { data: res } = await api.put("/devadmin/voice-config", { active_provider: provider.id });
        setData(res); hydrate(res);
        toast.success(`${provider.label} is now the active provider`);
      } else {
        toast.success(`${provider.label} settings saved`);
      }
    } catch (e) {
      toast.error(apiErr(e.response?.data?.detail) || "Could not save");
    } finally { setSaving(false); }
  };

  const testConnection = async () => {
    if (!provider) return;
    setTesting(true);
    try {
      const { data: res } = await api.post(`/devadmin/voice-config/test/${provider.id}`);
      toast.success(`Connection OK — ${res.detail} (${res.label})`);
    } catch (e) {
      toast.error(apiErr(e.response?.data?.detail) || "Test failed");
    } finally { setTesting(false); }
  };

  const addProvider = async () => {
    if (!newLabel.trim()) { toast.error("Enter a provider name"); return; }
    setAdding(true);
    try {
      const { data: res } = await api.post("/devadmin/voice-config/providers", {
        label: newLabel.trim(), adapter_type: newType, docs_url: newDocs.trim() || null,
      });
      setData(res); hydrate(res);
      const added = res.providers[res.providers.length - 1];
      setTab(added?.id || tab);
      toast.success(`Added "${newLabel.trim()}"`);
      setAddOpen(false); setNewLabel(""); setNewDocs(""); setNewType("webhook");
    } catch (e) {
      toast.error(apiErr(e.response?.data?.detail) || "Could not add provider");
    } finally { setAdding(false); }
  };

  const placeTestCall = async () => {
    if (!provider) return;
    if (!testNumber.trim()) { toast.error("Enter a phone number (E.164)"); return; }
    setPlacingCall(true);
    try {
      const { data: res } = await api.post(`/devadmin/voice-config/test-call/${provider.id}`, { to_number: testNumber.trim() });
      toast.success(`Test call placed via ${res.label}${res.call_id ? ` (id ${String(res.call_id).slice(0, 12)}…)` : ""}`);
    } catch (e) {
      toast.error(apiErr(e.response?.data?.detail) || "Test call failed");
    } finally { setPlacingCall(false); }
  };

  const removeProvider = async () => {
    if (!removeTarget) return;
    try {
      const { data: res } = await api.delete(`/devadmin/voice-config/providers/${removeTarget.id}`);
      setData(res); hydrate(res);
      setTab(res.active_provider || "retell");
      setRemoveTarget(null);
      toast.success("Provider removed");
    } catch (e) {
      toast.error(apiErr(e.response?.data?.detail) || "Could not remove");
    }
  };

  // ---- Retell per-call-type routing ----
  const loadRoutes = useCallback(async () => {
    try {
      const { data } = await api.get("/devadmin/voice-config/retell/routes");
      setRoutes(data.routes);
    } catch (e) { /* leave routes null; section hides */ }
  }, []);

  useEffect(() => { if (tab === "retell") loadRoutes(); }, [tab, loadRoutes]);

  // ---- Auto-retry config ----
  const loadAutoRetry = useCallback(async () => {
    try {
      const { data } = await api.get("/devadmin/voice-config/auto-retry");
      setAutoRetry(data);
    } catch (e) { /* hide section on error */ }
  }, []);

  useEffect(() => { if (tab === "retell") loadAutoRetry(); }, [tab, loadAutoRetry]);

  const loadCallQueue = useCallback(async () => {
    try {
      const { data } = await api.get("/devadmin/voice-config/call-queue");
      setCallQueue(data);
    } catch (e) { /* hide section on error */ }
  }, []);

  useEffect(() => { if (tab === "retell") loadCallQueue(); }, [tab, loadCallQueue]);

  const saveCallQueue = async () => {
    setSavingQueue(true);
    try {
      const { data } = await api.put("/devadmin/voice-config/call-queue", {
        max_parallel: Math.max(1, Math.min(parseInt(callQueue.max_parallel, 10) || 2, callQueue.max_allowed || 20)),
      });
      setCallQueue((c) => ({ ...c, ...data }));
      toast.success(`Call queue set to ${data.max_parallel} simultaneous call${data.max_parallel > 1 ? "s" : ""}`);
    } catch (e) {
      toast.error(apiErr(e.response?.data?.detail) || "Could not save call-queue settings");
    } finally { setSavingQueue(false); }
  };

  const webhookUrl = callQueue?.webhook_path
    ? `${(process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "")}${callQueue.webhook_path}`
    : "";
  const copyWebhook = async () => {
    try {
      await navigator.clipboard.writeText(webhookUrl);
      toast.success("Webhook URL copied");
    } catch { toast.error("Copy failed — select and copy manually"); }
  };

  const saveAutoRetry = async () => {
    setSavingRetry(true);
    try {
      const { data } = await api.put("/devadmin/voice-config/auto-retry", {
        enabled: autoRetry.enabled,
        delay_min: Math.max(1, Math.min(parseInt(autoRetry.delay_min, 10) || 30, 1440)),
      });
      setAutoRetry(data);
      toast.success(data.enabled ? `Auto-retry ON · once after ${data.delay_min} min` : "Auto-retry turned OFF");
    } catch (e) {
      toast.error(apiErr(e.response?.data?.detail) || "Could not save auto-retry settings");
    } finally { setSavingRetry(false); }
  };

  const refreshAgents = async () => {
    setLoadingAgents(true);
    try {
      const { data } = await api.get("/devadmin/voice-config/retell/agents");
      setAgents(data.agents || []);
      toast.success(`Loaded ${data.agents?.length || 0} agent(s) from Retell`);
    } catch (e) {
      toast.error(apiErr(e.response?.data?.detail) || "Could not fetch agents — save the API key first");
    } finally { setLoadingAgents(false); }
  };

  const saveRoutes = async () => {
    setSavingRoutes(true);
    try {
      const { data } = await api.put("/devadmin/voice-config/retell/routes", { routes });
      setRoutes(data.routes);
      toast.success("Call routing saved — calls now route to the mapped agents");
    } catch (e) {
      toast.error(apiErr(e.response?.data?.detail) || "Could not save routing");
    } finally { setSavingRoutes(false); }
  };

  const setRoute = (kind, key, value) =>
    setRoutes((r) => ({ ...r, [kind]: { ...(r?.[kind] || {}), [key]: value } }));

  // Agent options for a dropdown; include the saved id even if not in the fetched list.
  const agentOptions = (currentId) => {
    const list = [...agents];
    if (currentId && !list.some((a) => a.agent_id === currentId)) {
      list.unshift({ agent_id: currentId, name: `${currentId} (saved)` });
    }
    return list;
  };

  if (loading || !data) {
    const spin = <div className="py-24 text-center text-slate-400"><Loader2 className="h-6 w-6 mx-auto animate-spin" /></div>;
    return embedded ? spin : <Layout>{spin}</Layout>;
  }

  const isActive = data.active_provider === tab;

  const content = (
      <div className="space-y-6 max-w-4xl" data-testid="voice-config-page">
        {!embedded && (
          <div>
            <h1 className="text-3xl font-bold text-slate-900 dark:text-slate-100 flex items-center gap-2" style={{ fontFamily: "Outfit" }}>
              <PhoneCall className="h-7 w-7 text-indigo-500" /> Voice Agents
            </h1>
            <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
              Choose the voice-call provider and manage its credentials. Saved values override environment defaults — no redeploy needed.
            </p>
          </div>
        )}

        {/* Provider tabs */}
        <div className="flex flex-wrap gap-2" data-testid="voice-provider-tabs">
          {data.providers.map((p) => {
            const active = data.active_provider === p.id;
            const selected = tab === p.id;
            return (
              <button
                key={p.id}
                onClick={() => setTab(p.id)}
                className={`relative px-4 py-2.5 rounded-xl text-sm font-medium border transition-colors ${selected ? "bg-indigo-600 text-white border-indigo-600" : "bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-300 hover:border-indigo-300"}`}
                data-testid={`voice-tab-${p.id}`}
              >
                {p.label}
                {active && <BadgeCheck className={`inline h-3.5 w-3.5 ml-1.5 -mt-0.5 ${selected ? "text-white" : "text-emerald-500"}`} />}
                {!p.implemented && <span className={`ml-1.5 text-[9px] uppercase tracking-wider px-1 py-0.5 rounded ${selected ? "bg-white/20" : "bg-amber-100 dark:bg-amber-900/40 text-amber-600 dark:text-amber-300"}`}>soon</span>}
              </button>
            );
          })}
          <button
            onClick={() => setAddOpen(true)}
            className="px-4 py-2.5 rounded-xl text-sm font-medium border border-dashed border-slate-300 dark:border-slate-600 text-slate-500 dark:text-slate-400 hover:border-indigo-400 hover:text-indigo-500 transition-colors inline-flex items-center gap-1.5"
            data-testid="voice-add-provider-btn"
          >
            <Plus className="h-4 w-4" /> Add provider
          </button>
        </div>

        {/* Selected provider card */}
        {provider && (
          <div className="rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 p-5 space-y-4" data-testid={`voice-card-${provider.id}`}>
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div>
                <div className="flex items-center gap-2">
                  <h2 className="text-lg font-bold text-slate-900 dark:text-slate-100" style={{ fontFamily: "Outfit" }}>{provider.label}</h2>
                  {isActive ? (
                    <span className="inline-flex items-center gap-1 text-xs font-semibold px-2 py-0.5 rounded-full bg-emerald-100 dark:bg-emerald-900/40 text-emerald-700 dark:text-emerald-300" data-testid="voice-active-badge"><CheckCircle2 className="h-3 w-3" /> Active</span>
                  ) : null}
                </div>
                {!provider.implemented && (
                  <p className="text-xs text-amber-600 dark:text-amber-400 mt-1 flex items-center gap-1">
                    <AlertTriangle className="h-3.5 w-3.5" /> Not yet integrated for calls — config is saved for when this provider goes live.
                  </p>
                )}
                {provider.docs_url && (
                  <a href={provider.docs_url} target="_blank" rel="noreferrer" className="text-xs text-indigo-500 hover:underline inline-flex items-center gap-1 mt-1">
                    Documentation <ExternalLink className="h-3 w-3" />
                  </a>
                )}
              </div>
              {!isActive && (
                <Button variant="outline" size="sm" className="rounded-full" onClick={() => save(true)} disabled={saving} data-testid="voice-set-active-btn">
                  {saving ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <Zap className="h-3.5 w-3.5 mr-1" />} Save, test & set active
                </Button>
              )}
              {!provider.builtin && (
                <Button variant="ghost" size="sm" className="rounded-full text-rose-600 hover:bg-rose-50 dark:hover:bg-rose-900/30" onClick={() => setRemoveTarget(provider)} data-testid="voice-remove-provider-btn">
                  <Trash2 className="h-3.5 w-3.5 mr-1" /> Remove
                </Button>
              )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {provider.fields.map((fld) => {
                const src = provider.sources?.[fld.key];
                const revealKey = `${provider.id}.${fld.key}`;
                return (
                  <div key={fld.key} className={fld.key === "base_url" || fld.key === "webhook_url" ? "md:col-span-2" : ""}>
                    <label className="flex items-center gap-2 text-[10px] font-semibold uppercase tracking-wider text-slate-400 mb-1">
                      {fld.label}
                      {src === "env" && <span className="text-[9px] px-1 py-0.5 rounded bg-slate-100 dark:bg-slate-700 text-slate-500 normal-case tracking-normal">from env</span>}
                    </label>
                    {fld.secret ? (
                      <div className="relative">
                        <Input
                          type={reveal[revealKey] ? "text" : "password"}
                          value={secrets[provider.id]?.[fld.key] ?? ""}
                          onChange={(e) => setSecrets((s) => ({ ...s, [provider.id]: { ...s[provider.id], [fld.key]: e.target.value } }))}
                          placeholder={provider[`has_${fld.key}`] ? `${provider.values[fld.key]} — leave blank to keep` : (fld.placeholder || "Not set")}
                          className="rounded-xl pr-9"
                          data-testid={`voice-field-${provider.id}-${fld.key}`}
                        />
                        <button type="button" onClick={() => setReveal((r) => ({ ...r, [revealKey]: !r[revealKey] }))} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600" tabIndex={-1}>
                          {reveal[revealKey] ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                        </button>
                      </div>
                    ) : (
                      <Input
                        value={form[provider.id]?.[fld.key] ?? ""}
                        onChange={(e) => setForm((f) => ({ ...f, [provider.id]: { ...f[provider.id], [fld.key]: e.target.value } }))}
                        placeholder={fld.placeholder || ""}
                        className="rounded-xl"
                        data-testid={`voice-field-${provider.id}-${fld.key}`}
                      />
                    )}
                  </div>
                );
              })}
            </div>

            {provider.implemented && (
              <div className="rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900/40 p-3">
                <label className="block text-[10px] font-semibold uppercase tracking-wider text-slate-400 mb-1.5">Place a real test call</label>
                <div className="flex items-center gap-2 flex-wrap">
                  <Input value={testNumber} onChange={(e) => setTestNumber(e.target.value)} placeholder="+9198XXXXXXXX" className="rounded-xl flex-1 min-w-[180px]" data-testid="voice-testcall-number" />
                  <Button variant="outline" className="rounded-full" onClick={placeTestCall} disabled={placingCall || !testNumber.trim()} data-testid="voice-testcall-btn">
                    {placingCall ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <PhoneCall className="h-4 w-4 mr-1.5" />} Call this number
                  </Button>
                </div>
                <p className="text-[11px] text-slate-400 mt-1.5">Dials a real call using {provider.label}'s saved config — hear it before pointing at parents.</p>
              </div>
            )}

            {/* Per-call-type agent routing (Retell only) */}
            {provider.id === "retell" && routes && (
              <div className="rounded-xl border border-indigo-200 dark:border-indigo-800/60 bg-indigo-50/40 dark:bg-indigo-950/20 p-4 space-y-3" data-testid="retell-routing">
                <div className="flex items-start justify-between gap-2 flex-wrap">
                  <div>
                    <h3 className="text-sm font-bold text-slate-900 dark:text-slate-100 flex items-center gap-1.5" style={{ fontFamily: "Outfit" }}>
                      <Route className="h-4 w-4 text-indigo-500" /> Call routing per type
                    </h3>
                    <p className="text-[11px] text-slate-500 dark:text-slate-400 mt-0.5">
                      Route each call type to its own Retell agent. Absence / Birthday / Announcement fall back to the Default agent when left unset.
                    </p>
                  </div>
                  <Button variant="outline" size="sm" className="rounded-full" onClick={refreshAgents} disabled={loadingAgents} data-testid="retell-refresh-agents">
                    {loadingAgents ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5 mr-1" />} Refresh agents
                  </Button>
                </div>
                {agents.length === 0 && (
                  <p className="text-[11px] text-amber-600 dark:text-amber-400 flex items-center gap-1">
                    <AlertTriangle className="h-3.5 w-3.5" /> Click "Refresh agents" to load your Retell agents into the dropdowns (needs the saved API key).
                  </p>
                )}
                {ROUTE_KINDS.map((k) => (
                  <div key={k} className="grid grid-cols-1 md:grid-cols-3 gap-2 items-end border-t border-slate-200/70 dark:border-slate-700/50 pt-3">
                    <div>
                      <label className="block text-[10px] font-semibold uppercase tracking-wider text-slate-400 mb-1">{ROUTE_LABELS[k]} — agent</label>
                      <Select value={routes[k]?.agent_id || "__none__"} onValueChange={(v) => setRoute(k, "agent_id", v === "__none__" ? "" : v)}>
                        <SelectTrigger className="rounded-xl" data-testid={`retell-agent-${k}`}><SelectValue placeholder="Select agent" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="__none__">— None (use Default)</SelectItem>
                          {agentOptions(routes[k]?.agent_id).map((a) => (
                            <SelectItem key={a.agent_id} value={a.agent_id}>{a.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <label className="block text-[10px] font-semibold uppercase tracking-wider text-slate-400 mb-1">From number (optional)</label>
                      <Input value={routes[k]?.from_number || ""} onChange={(e) => setRoute(k, "from_number", e.target.value)} placeholder="use default" className="rounded-xl" data-testid={`retell-from-${k}`} />
                    </div>
                    <div>
                      <label className="block text-[10px] font-semibold uppercase tracking-wider text-slate-400 mb-1">Voice / language hint (optional)</label>
                      <Input value={routes[k]?.voice_hint || ""} onChange={(e) => setRoute(k, "voice_hint", e.target.value)} placeholder="e.g. Kannada" className="rounded-xl" data-testid={`retell-hint-${k}`} />
                    </div>
                  </div>
                ))}
                <div className="flex justify-end pt-1">
                  <Button onClick={saveRoutes} disabled={savingRoutes} className="rounded-full bg-indigo-600 hover:bg-indigo-700" data-testid="retell-save-routes">
                    {savingRoutes ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Save className="h-4 w-4 mr-1.5" />} Save routing
                  </Button>
                </div>
              </div>
            )}

            {tab === "retell" && autoRetry && (
              <div className="rounded-2xl border border-slate-200 dark:border-slate-700 p-4 space-y-4" data-testid="auto-retry-card">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <div className="flex items-center gap-2 text-sm font-semibold text-slate-900 dark:text-slate-100">
                      <RefreshCw className="h-4 w-4 text-indigo-500" /> Call auto-retry
                    </div>
                    <p className="text-xs text-slate-500 dark:text-slate-400 mt-1 max-w-md">
                      When a parent doesn't answer an announcement call, retry it <b>once</b> after the delay below. This is a single retry per call — never repeated.
                    </p>
                  </div>
                  <Switch
                    checked={!!autoRetry.enabled}
                    onCheckedChange={(v) => setAutoRetry((a) => ({ ...a, enabled: v }))}
                    data-testid="auto-retry-toggle"
                  />
                </div>
                <div className="flex items-end gap-3 flex-wrap">
                  <div>
                    <label className="block text-[10px] font-semibold uppercase tracking-wider text-slate-400 mb-1">Retry delay (minutes)</label>
                    <Input
                      type="number" min="1" max="1440"
                      value={autoRetry.delay_min ?? 30}
                      disabled={!autoRetry.enabled}
                      onChange={(e) => setAutoRetry((a) => ({ ...a, delay_min: e.target.value }))}
                      className="rounded-xl w-40"
                      data-testid="auto-retry-delay"
                    />
                  </div>
                  <Button onClick={saveAutoRetry} disabled={savingRetry} className="rounded-full bg-indigo-600 hover:bg-indigo-700" data-testid="auto-retry-save">
                    {savingRetry ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Save className="h-4 w-4 mr-1.5" />} Save auto-retry
                  </Button>
                </div>
              </div>
            )}

            {tab === "retell" && callQueue && (
              <div className="rounded-2xl border border-slate-200 dark:border-slate-700 p-4 space-y-4" data-testid="call-queue-card">
                <div>
                  <div className="flex items-center gap-2 text-sm font-semibold text-slate-900 dark:text-slate-100">
                    <Layers className="h-4 w-4 text-indigo-500" /> Call queue — simultaneous calls
                  </div>
                  <p className="text-xs text-slate-500 dark:text-slate-400 mt-1 max-w-md">
                    Maximum number of outbound calls that ring <b>at the same time</b>. When a batch has more, the rest wait in a queue and dial automatically as each call ends — so no call is ever dropped. Keep this at or below your Retell plan's concurrency limit.
                  </p>
                </div>
                <div className="flex items-end gap-3 flex-wrap">
                  <div>
                    <label className="block text-[10px] font-semibold uppercase tracking-wider text-slate-400 mb-1">
                      Simultaneous calls (1–{callQueue.max_allowed || 20})
                    </label>
                    <Input
                      type="number" min="1" max={callQueue.max_allowed || 20}
                      value={callQueue.max_parallel ?? 2}
                      onChange={(e) => setCallQueue((c) => ({ ...c, max_parallel: e.target.value }))}
                      className="rounded-xl w-40"
                      data-testid="call-queue-max"
                    />
                  </div>
                  <Button onClick={saveCallQueue} disabled={savingQueue} className="rounded-full bg-indigo-600 hover:bg-indigo-700" data-testid="call-queue-save">
                    {savingQueue ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Save className="h-4 w-4 mr-1.5" />} Save queue limit
                  </Button>
                  <span className="text-[11px] text-slate-400">Default: {callQueue.default ?? 2}</span>
                </div>

                <div className="pt-2 border-t border-slate-100 dark:border-slate-800">
                  <div className="flex items-center gap-2 text-sm font-semibold text-slate-900 dark:text-slate-100">
                    <Webhook className="h-4 w-4 text-indigo-500" /> Retell webhook URL
                  </div>
                  <p className="text-xs text-slate-500 dark:text-slate-400 mt-1 max-w-md">
                    Paste this into your Retell dashboard (Agent → Webhook / Call events). It lets the queue free a slot the instant a call ends, so the next queued call dials immediately. Without it, the queue still works but drains a little slower (polling every few seconds).
                  </p>
                  <div className="flex items-center gap-2 mt-2">
                    <Input readOnly value={webhookUrl} className="rounded-xl font-mono text-xs" data-testid="retell-webhook-url" onFocus={(e) => e.target.select()} />
                    <Button variant="outline" onClick={copyWebhook} className="rounded-full shrink-0" data-testid="retell-webhook-copy">
                      <Copy className="h-4 w-4 mr-1.5" /> Copy
                    </Button>
                  </div>
                  <p className="text-[11px] text-slate-400 mt-1.5">This URL matches the site you're viewing — open this page on your production site to get the production webhook URL.</p>
                </div>
              </div>
            )}

            <div className="flex items-center justify-between gap-2 flex-wrap pt-2">
              {provider.implemented ? (
                <Button variant="outline" className="rounded-full" onClick={testConnection} disabled={testing} data-testid="voice-test-btn">
                  {testing ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Zap className="h-4 w-4 mr-1.5" />} Test connection
                </Button>
              ) : <span />}
              <Button onClick={() => save(false)} disabled={saving} className="rounded-full bg-indigo-600 hover:bg-indigo-700" data-testid="voice-save-btn">
                {saving ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Save className="h-4 w-4 mr-1.5" />} Save settings
              </Button>
            </div>
          </div>
        )}

        {/* Add provider dialog */}
        <Dialog open={addOpen} onOpenChange={(o) => { if (!o) setAddOpen(false); }}>
          <DialogContent className="max-w-md" data-testid="voice-add-dialog">
            <DialogHeader>
              <DialogTitle style={{ fontFamily: "Outfit" }} className="flex items-center gap-2"><Plus className="h-5 w-5 text-indigo-500" /> Add voice provider</DialogTitle>
              <DialogDescription>Register a new provider. Choose how calls are placed — a Retell-compatible API, or a generic webhook to your own service.</DialogDescription>
            </DialogHeader>
            <div className="space-y-3">
              <div>
                <label className="block text-[10px] font-semibold uppercase tracking-wider text-slate-400 mb-1">Provider name</label>
                <Input value={newLabel} onChange={(e) => setNewLabel(e.target.value)} placeholder="e.g. Plivo, Exotel, My Voice Bot" maxLength={40} data-testid="voice-add-label" />
              </div>
              <div>
                <label className="block text-[10px] font-semibold uppercase tracking-wider text-slate-400 mb-1">Call integration type</label>
                <Select value={newType} onValueChange={setNewType}>
                  <SelectTrigger data-testid="voice-add-type"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="webhook">Webhook (POST to your service)</SelectItem>
                    <SelectItem value="retell">Retell-compatible API</SelectItem>
                    <SelectItem value="bolna">Bolna API</SelectItem>
                    <SelectItem value="vapi">Vapi API</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-[11px] text-slate-400 mt-1">
                  {newType === "webhook"
                    ? "We POST {to, from, message, variables} to your URL for each call."
                    : newType === "retell" ? "Uses the Retell call API with the credentials you provide."
                    : newType === "bolna" ? "Uses the Bolna /call API with the credentials you provide."
                    : "Uses the Vapi /call API with the credentials you provide."}
                </p>
              </div>
              <div>
                <label className="block text-[10px] font-semibold uppercase tracking-wider text-slate-400 mb-1">Docs URL (optional)</label>
                <Input value={newDocs} onChange={(e) => setNewDocs(e.target.value)} placeholder="https://…" data-testid="voice-add-docs" />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setAddOpen(false)} disabled={adding}>Cancel</Button>
              <Button className="bg-indigo-600 hover:bg-indigo-700" onClick={addProvider} disabled={adding || !newLabel.trim()} data-testid="voice-add-confirm">
                {adding ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Plus className="h-4 w-4 mr-1.5" />} Add provider
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Remove provider dialog */}
        <Dialog open={!!removeTarget} onOpenChange={(o) => { if (!o) setRemoveTarget(null); }}>
          <DialogContent className="max-w-md" data-testid="voice-remove-dialog">
            <DialogHeader>
              <DialogTitle style={{ fontFamily: "Outfit" }} className="flex items-center gap-2 text-rose-600"><Trash2 className="h-5 w-5" /> Remove provider?</DialogTitle>
              <DialogDescription>This removes <span className="font-semibold">{removeTarget?.label}</span> and its saved credentials. If it was the active provider, calls fall back to Retell.</DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button variant="outline" onClick={() => setRemoveTarget(null)}>Cancel</Button>
              <Button className="bg-rose-500 hover:bg-rose-600" onClick={removeProvider} data-testid="voice-remove-confirm">Remove</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
  );

  if (embedded) return content;
  return <Layout>{content}</Layout>;
}
